import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { FileText, Download, TrendingUp, TrendingDown, DollarSign, Users, Calendar, Filter, Search } from 'lucide-react';
import { reports } from '../api';
import { useToast } from '../hooks/use-toast';
import { formatMoney } from '../utils/currency';
import { TrialBalanceReport, GeneralLedgerReport, AgingReport } from './AccountingReports';

const ACCOUNTING_TYPES = ['trial-balance', 'general-ledger', 'ar-aging', 'ap-aging'];

const Reports = ({ businessId, businessName, businessCurrency = 'USD', dashboardData }) => {
  const fmt = (amt) => formatMoney(amt, businessCurrency);
  const { toast } = useToast();
  const [accountingData, setAccountingData] = useState(null);
  const [glAccount, setGlAccount] = useState('');
  const [reportType, setReportType] = useState('sales');
  const [timeRange, setTimeRange] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  
  // Report data states
  const [salesData, setSalesData] = useState(null);
  const [expenseData, setExpenseData] = useState(null);
  const [balanceData, setBalanceData] = useState(null);
  const [cashFlowData, setCashFlowData] = useState(null);
  const [divisionPnl, setDivisionPnl] = useState(null);
  const [includeAllocation, setIncludeAllocation] = useState(true);
  const [loading, setLoading] = useState(false);

  // Small helper to render a per-division breakdown card when the parent is a Legal Entity (Holding Company).
  const renderSubsidiaryBreakdown = (data, kind) => {
    if (!data || !data.is_holding_company || !data.subsidiary_breakdown || data.subsidiary_breakdown.length <= 1) return null;
    const rows = data.subsidiary_breakdown;
    const currency = data.currency || businessCurrency;
    
    // Columns per report type
    const columns = {
      sales: [
        { key: 'invoiced_converted', label: 'Invoiced', className: 'text-right' },
        { key: 'paid_converted', label: 'Paid', className: 'text-right text-green-700' },
      ],
      expenses: [
        { key: 'expense_count', label: '#', className: 'text-right', money: false },
        { key: 'expenses_converted', label: 'Expenses', className: 'text-right text-red-700' },
      ],
      balance: [
        { key: 'assets_converted', label: 'Assets Assigned', className: 'text-right text-blue-700' },
        { key: 'liabilities_converted', label: 'Liabilities Assigned', className: 'text-right text-red-700' },
        { key: 'equity_converted', label: 'Net Assets', className: 'text-right text-green-700' },
      ],
      cashflow: [
        { key: 'inflow_converted', label: 'Inflow', className: 'text-right text-green-700' },
        { key: 'outflow_converted', label: 'Outflow', className: 'text-right text-red-700' },
        { key: 'transfers_in_converted', label: 'Transfers In', className: 'text-right text-blue-700' },
        { key: 'transfers_out_converted', label: 'Transfers Out', className: 'text-right text-blue-700' },
        { key: 'net_converted', label: 'Net', className: 'text-right' },
      ],
    }[kind] || [];
    
    return (
      <Card className="border-2 border-yellow-300 bg-yellow-50/40" data-testid={`holding-division-breakdown-${kind}`}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-yellow-900">
            <span className="text-xs font-bold uppercase tracking-wider bg-yellow-400 text-yellow-900 px-2 py-1 rounded">Legal Entity</span>
            Consolidated by Division
          </CardTitle>
          <p className="text-xs text-yellow-800 mt-1">Internal divisional management reporting for the legal entity. Amounts converted to {currency} using live FX rates. Divisions are operating units of this legal company and are not separate legal entities.</p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-yellow-100">
                <tr>
                  <th className="text-left py-2 px-3">Division</th>
                  <th className="text-left py-2 px-3">Currency</th>
                  {columns.map(c => (
                    <th key={c.key} className={`py-2 px-3 ${c.className.includes('text-right') ? 'text-right' : ''}`}>{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-yellow-200">
                {rows.map((r) => (
                  <tr key={r.business_id} className="hover:bg-yellow-100/40">
                    <td className="py-2 px-3 font-medium">
                      {r.display_name || r.business_name || '—'}
                      {r.is_corporate_head_office && (
                        <span className="ml-2 text-[10px] uppercase font-bold bg-blue-200 text-blue-900 px-1.5 py-0.5 rounded">Corporate</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-xs text-gray-600">{r.currency}</td>
                    {columns.map(c => (
                      <td key={c.key} className={`py-2 px-3 ${c.className}`}>
                        {c.money === false ? (r[c.key] || 0) : formatMoney(r[c.key] || 0, currency)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Auto-fetch when time range or dates change (for preset ranges)
  useEffect(() => {
    if (!businessId) return;
    if (timeRange === 'custom') return;
    
    if (reportType === 'sales') {
      fetchSalesReport();
    } else if (reportType === 'expenses') {
      fetchExpenseReport();
    } else if (reportType === 'balance') {
      fetchBalanceSheet();
    } else if (reportType === 'cashflow') {
      fetchCashFlow();
    } else if (reportType === 'division-pnl') {
      fetchDivisionPnl();
    } else if (ACCOUNTING_TYPES.includes(reportType)) {
      fetchAccounting();
    }
  }, [businessId, reportType, timeRange, startDate, endDate, categoryFilter, includeAllocation, glAccount]);

  const handleCustomSearch = () => {
    if (reportType === 'sales') {
      fetchSalesReport();
    } else if (reportType === 'expenses') {
      fetchExpenseReport();
    } else if (reportType === 'balance') {
      fetchBalanceSheet();
    } else if (reportType === 'cashflow') {
      fetchCashFlow();
    } else if (reportType === 'division-pnl') {
      fetchDivisionPnl();
    } else if (ACCOUNTING_TYPES.includes(reportType)) {
      fetchAccounting();
    }
  };

  const accountingParams = () => {
    if (reportType === 'ar-aging' || reportType === 'ap-aging') return endDate ? { as_of: endDate } : {};
    const p = {};
    if (startDate) p.start_date = startDate;
    if (endDate) p.end_date = endDate;
    if (reportType === 'general-ledger' && glAccount) p.account = glAccount;
    return p;
  };

  const fetchAccounting = async () => {
    setLoading(true);
    try {
      const fn = { 'trial-balance': reports.getTrialBalance, 'general-ledger': reports.getGeneralLedger, 'ar-aging': reports.getArAging, 'ap-aging': reports.getApAging }[reportType];
      const response = await fn(businessId, accountingParams());
      setAccountingData({ ...response.data, _type: reportType });
    } catch (error) {
      toast({ title: 'Error', description: error?.response?.data?.detail || 'Failed to load report', variant: 'destructive' });
      setAccountingData(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchSalesReport = async () => {
    try {
      setLoading(true);
      const response = await reports.getSales(businessId, startDate || null, endDate || null);
      setSalesData(response.data);
    } catch (error) {
      console.error('Error fetching sales report:', error);
      toast({
        title: 'Error',
        description: 'Failed to load sales report',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchExpenseReport = async () => {
    try {
      setLoading(true);
      const response = await reports.getExpenses(
        businessId, 
        startDate || null, 
        endDate || null, 
        categoryFilter || null
      );
      setExpenseData(response.data);
    } catch (error) {
      console.error('Error fetching expense report:', error);
      toast({
        title: 'Error',
        description: 'Failed to load expense report',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchBalanceSheet = async () => {
    setLoading(true);
    try {
      const response = await reports.getBalanceSheet(businessId, startDate || null, endDate || null);
      setBalanceData(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load balance sheet', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchCashFlow = async () => {
    setLoading(true);
    try {
      const response = await reports.getCashFlow(businessId, startDate || null, endDate || null);
      setCashFlowData(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load cash flow', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchDivisionPnl = async () => {
    setLoading(true);
    try {
      const response = await reports.getDivisionPnl(businessId, startDate || null, endDate || null, includeAllocation);
      setDivisionPnl(response.data);
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Failed to load Division P&L';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
      setDivisionPnl(null);
    } finally {
      setLoading(false);
    }
  };

  const handleExportReport = async (format) => {
    try {
      let response;
      let filename;
      
      if (reportType === 'sales') {
        response = await reports.exportSales(businessId, format, startDate || null, endDate || null);
        filename = `sales_report.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      } else if (reportType === 'expenses') {
        response = await reports.exportExpenses(businessId, format, startDate || null, endDate || null, categoryFilter || null);
        filename = `expense_report.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      } else if (reportType === 'balance') {
        response = await reports.exportBalanceSheet(businessId, format, startDate || null, endDate || null);
        filename = `balance_sheet.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      } else if (reportType === 'cashflow') {
        response = await reports.exportCashFlow(businessId, format, startDate || null, endDate || null);
        filename = `cash_flow.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      } else if (ACCOUNTING_TYPES.includes(reportType)) {
        response = await reports.exportAccounting(businessId, reportType, format, accountingParams());
        filename = `${reportType.replace('-', '_')}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      } else {
        response = await reports.export(businessId, reportType, format);
        filename = `${reportType}_report.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      }
      
      const blob = new Blob([response.data], { 
        type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast({
        title: 'Export Successful',
        description: `Report exported as ${format.toUpperCase()}`,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to export report',
        variant: 'destructive'
      });
    }
  };

  const handleTimeRangeChange = (value) => {
    setTimeRange(value);
    
    // For custom, just show the date inputs - user will click Search
    if (value === 'custom') {
      return;
    }
    
    const today = new Date();
    let start = '';
    let end = today.toISOString().split('T')[0];
    
    switch (value) {
      case 'today':
        start = end;
        break;
      case 'week':
        const weekAgo = new Date(new Date().setDate(new Date().getDate() - 7));
        start = weekAgo.toISOString().split('T')[0];
        end = new Date().toISOString().split('T')[0];
        break;
      case 'month':
        const monthAgo = new Date(new Date().setMonth(new Date().getMonth() - 1));
        start = monthAgo.toISOString().split('T')[0];
        end = new Date().toISOString().split('T')[0];
        break;
      case 'quarter':
        const quarterAgo = new Date(new Date().setMonth(new Date().getMonth() - 3));
        start = quarterAgo.toISOString().split('T')[0];
        end = new Date().toISOString().split('T')[0];
        break;
      case 'year':
        const yearAgo = new Date(new Date().setFullYear(new Date().getFullYear() - 1));
        start = yearAgo.toISOString().split('T')[0];
        end = new Date().toISOString().split('T')[0];
        break;
      case 'all':
      default:
        start = '';
        end = '';
    }
    
    setStartDate(start);
    setEndDate(end);
  };

  const renderSalesReport = () => {
    if (!salesData) return null;
    
    return (
      <div className="space-y-6">
        {renderSubsidiaryBreakdown(salesData, 'sales')}
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-blue-600 mb-2">
                <DollarSign className="h-5 w-5" />
                <span className="text-sm font-medium">Total Invoiced</span>
              </div>
              <div className="text-2xl font-bold text-blue-800">{formatMoney(salesData.total_invoiced || 0, businessCurrency)}</div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-green-600 mb-2">
                <TrendingUp className="h-5 w-5" />
                <span className="text-sm font-medium">Total Paid</span>
              </div>
              <div className="text-2xl font-bold text-green-800">{formatMoney(salesData.total_paid || 0, businessCurrency)}</div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-yellow-600 mb-2">
                <Calendar className="h-5 w-5" />
                <span className="text-sm font-medium">Pending</span>
              </div>
              <div className="text-2xl font-bold text-yellow-800">{formatMoney(salesData.total_pending || 0, businessCurrency)}</div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-purple-600 mb-2">
                <DollarSign className="h-5 w-5" />
                <span className="text-sm font-medium">Cash Injections</span>
              </div>
              <div className="text-2xl font-bold text-purple-800">{formatMoney(salesData.total_cash_injections || 0, businessCurrency)}</div>
            </CardContent>
          </Card>
        </div>

        {/* Grand Total */}
        <Card className="bg-gradient-to-r from-blue-600 to-blue-800 text-white">
          <CardContent className="p-6">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-blue-100 text-sm">Grand Total (Paid + Cash)</p>
                <p className="text-4xl font-bold">{formatMoney(salesData.grand_total || 0, businessCurrency)}</p>
              </div>
              <div className="text-right">
                <p className="text-blue-100 text-sm">{salesData.invoice_count || 0} Invoices</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sales by Client */}
        {salesData.sales_by_client && salesData.sales_by_client.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-600" />
                Sales by Client
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-blue-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-blue-800">Client</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-blue-800">Invoiced</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-blue-800">Paid</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-blue-800">Pending</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {salesData.sales_by_client.map((item, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium">{item.client}</td>
                        <td className="px-4 py-3 text-right">{formatMoney(item.invoiced || 0, businessCurrency)}</td>
                        <td className="px-4 py-3 text-right text-green-600">{formatMoney(item.paid || 0, businessCurrency)}</td>
                        <td className="px-4 py-3 text-right text-yellow-600">{formatMoney(item.pending || 0, businessCurrency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Sales by Month */}
        {salesData.sales_by_month && salesData.sales_by_month.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-green-600" />
                Sales by Month
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {salesData.sales_by_month.map((item, idx) => {
                  const maxAmount = Math.max(...salesData.sales_by_month.map(m => m.amount));
                  const percentage = maxAmount > 0 ? (item.amount / maxAmount) * 100 : 0;
                  return (
                    <div key={idx} className="flex items-center gap-4">
                      <span className="w-20 text-sm font-medium text-gray-600">{item.month}</span>
                      <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                        <div 
                          className="bg-gradient-to-r from-green-400 to-green-600 h-full rounded-full flex items-center justify-end pr-2"
                          style={{ width: `${Math.max(percentage, 10)}%` }}
                        >
                          <span className="text-xs font-semibold text-white">{formatMoney(item.amount || 0, businessCurrency)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  const renderExpenseReport = () => {
    if (!expenseData) return null;
    
    return (
      <div className="space-y-6">
        {renderSubsidiaryBreakdown(expenseData, 'expenses')}
        {/* Summary */}
        <div className="grid grid-cols-2 gap-4">
          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 text-red-600 mb-2">
                <TrendingDown className="h-5 w-5" />
                <span className="text-sm font-medium">Total Expenses</span>
              </div>
              <div className="text-3xl font-bold text-red-800">{formatMoney(expenseData.total_expenses || 0, businessCurrency)}</div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 text-orange-600 mb-2">
                <FileText className="h-5 w-5" />
                <span className="text-sm font-medium">Number of Expenses</span>
              </div>
              <div className="text-3xl font-bold text-orange-800">{expenseData.expense_count || 0}</div>
            </CardContent>
          </Card>
        </div>

        {/* No expenses message */}
        {expenseData.expense_count === 0 && (
          <Card className="border-dashed">
            <CardContent className="p-8 text-center">
              <TrendingDown className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No expenses recorded yet</p>
              <p className="text-gray-400 text-sm mt-2">Add expenses in the Expenses tab to see them in reports</p>
            </CardContent>
          </Card>
        )}

        {/* Category Filter */}
        {expenseData.available_categories && expenseData.available_categories.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <Filter className="h-5 w-5 text-gray-500" />
                <Label className="font-medium">Filter by Category:</Label>
                <Select value={categoryFilter || "all"} onValueChange={(val) => setCategoryFilter(val === "all" ? "" : val)}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {expenseData.available_categories.map((cat, idx) => (
                      <SelectItem key={idx} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {categoryFilter && (
                  <Button variant="ghost" size="sm" onClick={() => setCategoryFilter('')}>
                    Clear Filter
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Expenses by Category */}
        {expenseData.expenses_by_category && expenseData.expenses_by_category.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingDown className="h-5 w-5 text-red-600" />
                Expenses by Category
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {expenseData.expenses_by_category.map((item, idx) => (
                  <div key={idx} className="space-y-1">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-800">{item.category}</span>
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-gray-500">{item.percentage?.toFixed(1)}%</span>
                        <span className="font-semibold text-red-600">{formatMoney(item.amount || 0, businessCurrency)}</span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-3">
                      <div 
                        className="bg-gradient-to-r from-red-400 to-red-600 h-3 rounded-full"
                        style={{ width: `${item.percentage || 0}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Expenses by Month */}
        {expenseData.expenses_by_month && expenseData.expenses_by_month.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-orange-600" />
                Expenses by Month
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-orange-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-orange-800">Month</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-orange-800">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {expenseData.expenses_by_month.map((item, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium">{item.month}</td>
                        <td className="px-4 py-3 text-right font-semibold text-red-600">{formatMoney(item.amount || 0, businessCurrency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Expense Details Table */}
        {expenseData.expenses && expenseData.expenses.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Expense Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Category</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Description</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Vendor</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {expenseData.expenses.slice(0, 20).map((exp, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm">{exp.date}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">{exp.category}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">{exp.description}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{exp.vendor || '-'}</td>
                        <td className="px-4 py-3 text-right font-semibold text-red-600">{formatMoney(exp.amount || 0, businessCurrency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {expenseData.expenses.length > 20 && (
                  <p className="text-center text-sm text-gray-500 mt-4">
                    Showing 20 of {expenseData.expenses.length} expenses. Export for full list.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  const renderDivisionPnl = () => {
    if (!divisionPnl) return null;
    const { divisions = [], company_total = {}, currency, legal_entity_name } = divisionPnl;
    return (
      <div className="space-y-6" data-testid="division-pnl-report">
        <Card className="border-2 border-blue-300 bg-blue-50/40">
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div>
                <CardTitle className="flex items-center gap-2 text-blue-900">
                  <span className="text-xs font-bold uppercase tracking-wider bg-blue-500 text-white px-2 py-1 rounded">Legal Entity</span>
                  Division Profit &amp; Loss — {legal_entity_name || businessName}
                </CardTitle>
                <p className="text-xs text-blue-800 mt-1">Internal management report. Divisions are operating units of the legal company and are not separate legal entities. Amounts in {currency}.</p>
              </div>
              <div className="flex items-center gap-2 bg-white border rounded-md px-3 py-1.5 text-sm">
                <Label htmlFor="include-alloc-toggle" className="text-xs cursor-pointer">Show Corporate Allocation</Label>
                <input
                  type="checkbox"
                  id="include-alloc-toggle"
                  checked={includeAllocation}
                  onChange={(e) => setIncludeAllocation(e.target.checked)}
                  className="h-4 w-4 accent-blue-600"
                  data-testid="include-allocation-toggle"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-blue-100">
                  <tr>
                    <th className="text-left py-2 px-3">Division</th>
                    <th className="py-2 px-3 text-right">Revenue</th>
                    <th className="py-2 px-3 text-right">Direct Costs</th>
                    <th className="py-2 px-3 text-right">Gross Profit</th>
                    <th className="py-2 px-3 text-right">Operating Expenses</th>
                    <th className="py-2 px-3 text-right">Op. Profit (Before)</th>
                    {includeAllocation && <th className="py-2 px-3 text-right">Corporate Allocation</th>}
                    <th className="py-2 px-3 text-right font-semibold">{includeAllocation ? 'Profit (After)' : 'Profit'}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-blue-200">
                  {divisions.map((r) => (
                    <tr key={r.division_id} className="hover:bg-blue-100/40" data-testid={`division-pnl-row-${r.division_id}`}>
                      <td className="py-2 px-3 font-medium">
                        {r.display_name}
                        {r.is_corporate_head_office && (
                          <span className="ml-2 text-[10px] uppercase font-bold bg-blue-200 text-blue-900 px-1.5 py-0.5 rounded">Corporate</span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-right text-green-700">{formatMoney(r.revenue, currency)}</td>
                      <td className="py-2 px-3 text-right text-red-600">{formatMoney(r.direct_costs, currency)}</td>
                      <td className="py-2 px-3 text-right">{formatMoney(r.gross_profit, currency)}</td>
                      <td className="py-2 px-3 text-right text-red-600">{formatMoney(r.operating_expenses, currency)}</td>
                      <td className="py-2 px-3 text-right">{formatMoney(r.operating_profit_before_allocation, currency)}</td>
                      {includeAllocation && (
                        <td className="py-2 px-3 text-right text-orange-700">{formatMoney(r.corporate_overhead_allocation, currency)}</td>
                      )}
                      <td className={`py-2 px-3 text-right font-semibold ${r.profit_after_allocation >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
                        {formatMoney(r.profit_after_allocation, currency)}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-blue-200 font-bold border-t-2 border-blue-400" data-testid="division-pnl-company-total">
                    <td className="py-2 px-3">{company_total.display_name || 'Company Total'}</td>
                    <td className="py-2 px-3 text-right text-green-800">{formatMoney(company_total.revenue, currency)}</td>
                    <td className="py-2 px-3 text-right text-red-700">{formatMoney(company_total.direct_costs, currency)}</td>
                    <td className="py-2 px-3 text-right">{formatMoney(company_total.gross_profit, currency)}</td>
                    <td className="py-2 px-3 text-right text-red-700">{formatMoney(company_total.operating_expenses, currency)}</td>
                    <td className="py-2 px-3 text-right">{formatMoney(company_total.operating_profit_before_allocation, currency)}</td>
                    {includeAllocation && (
                      <td className="py-2 px-3 text-right text-gray-500 italic">— eliminated —</td>
                    )}
                    <td className={`py-2 px-3 text-right ${company_total.profit_after_allocation >= 0 ? 'text-blue-800' : 'text-red-800'}`}>
                      {formatMoney(company_total.profit_after_allocation, currency)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            {includeAllocation && (
              <p className="text-xs text-gray-500 mt-3">
                Corporate overhead allocations are management-accounting entries only and do not affect the legal company's Profit &amp; Loss.
                The Company Total row eliminates them so the consolidated P&amp;L is never double-counted.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderProfitLoss = () => {
    if (!dashboardData) return null;
    
    const revenue = dashboardData.totalRevenue;
    const expenses = dashboardData.totalExpenses;
    const netIncome = revenue - expenses;
    const profitMargin = revenue > 0 ? ((netIncome / revenue) * 100).toFixed(1) : 0;

    return (
      <div className="space-y-6">
        <Card className="bg-gradient-to-br from-green-50 to-blue-50 border-2 border-green-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-800">
              <TrendingUp className="h-5 w-5" />
              Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-green-700">{formatMoney(revenue, businessCurrency)}</div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-800">
              <TrendingDown className="h-5 w-5" />
              Expenses
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-red-700">{formatMoney(expenses, businessCurrency)}</div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-300">
          <CardHeader>
            <CardTitle className="text-blue-800">Net Income</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-5xl font-bold text-blue-700 mb-4">{formatMoney(netIncome, businessCurrency)}</div>
            <div className="grid grid-cols-2 gap-4 mt-6">
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <p className="text-sm text-gray-600 mb-1">Profit Margin</p>
                <p className="text-2xl font-bold text-blue-600">{profitMargin}%</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <p className="text-sm text-gray-600 mb-1">Status</p>
                <p className="text-2xl font-bold text-blue-600">{netIncome >= 0 ? 'Profitable' : 'Loss'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  if (loading && !salesData && !expenseData) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading reports...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="shadow-md">
        <CardHeader>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <CardTitle className="text-xl font-bold text-gray-800">Reports - {businessName}</CardTitle>
              <p className="text-sm text-gray-600 mt-1">Generate and export comprehensive financial reports</p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => handleExportReport('pdf')}
                className="bg-blue-600 hover:bg-blue-700 gap-2"
                data-testid="export-pdf-btn"
              >
                <Download className="h-4 w-4" />
                Export PDF
              </Button>
              <Button
                onClick={() => handleExportReport('excel')}
                className="bg-green-600 hover:bg-green-700 gap-2"
                data-testid="export-excel-btn"
              >
                <Download className="h-4 w-4" />
                Export Excel
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-2 block">Report Type</Label>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger data-testid="report-type-select">
                  <FileText className="h-4 w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sales">Sales Report</SelectItem>
                  <SelectItem value="expenses">Expense Report</SelectItem>
                  <SelectItem value="balance">Balance Sheet</SelectItem>
                  <SelectItem value="cashflow">Cash Flow</SelectItem>
                  <SelectItem value="profit-loss">Profit & Loss Statement</SelectItem>
                  <SelectItem value="division-pnl">Division P&amp;L (Management)</SelectItem>
                  <SelectItem value="trial-balance">Trial Balance</SelectItem>
                  <SelectItem value="general-ledger">General Ledger</SelectItem>
                  <SelectItem value="ar-aging">Accounts Receivable (Aging)</SelectItem>
                  <SelectItem value="ap-aging">Accounts Payable (Aging)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-2 block">Time Range</Label>
              <Select value={timeRange} onValueChange={handleTimeRangeChange}>
                <SelectTrigger data-testid="time-range-select">
                  <Calendar className="h-4 w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="week">Last 7 Days</SelectItem>
                  <SelectItem value="month">Last 30 Days</SelectItem>
                  <SelectItem value="quarter">Last 3 Months</SelectItem>
                  <SelectItem value="year">Last Year</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Custom Date Range Row with Search Button */}
          {timeRange === 'custom' && (
            <div className="flex flex-wrap items-end gap-4 mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex-1 min-w-[140px]">
                <Label className="text-sm font-medium text-gray-700 mb-2 block">From</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="bg-white"
                  data-testid="custom-start-date"
                />
              </div>
              <div className="flex-1 min-w-[140px]">
                <Label className="text-sm font-medium text-gray-700 mb-2 block">To</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="bg-white"
                  data-testid="custom-end-date"
                />
              </div>
              <Button
                onClick={handleCustomSearch}
                className="bg-blue-600 hover:bg-blue-700 gap-2"
                data-testid="search-report-btn"
              >
                <Search className="h-4 w-4" />
                Search
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setStartDate('');
                  setEndDate('');
                  setTimeRange('all');
                }}
                data-testid="clear-dates-btn"
              >
                Clear
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {loading && (
        <div className="text-center py-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      )}

      {!loading && reportType === 'sales' && renderSalesReport()}
      {!loading && reportType === 'expenses' && renderExpenseReport()}
      {!loading && reportType === 'balance' && balanceData && (
        <div className="space-y-6" data-testid="balance-sheet-report">
          {renderSubsidiaryBreakdown(balanceData, 'balance')}
          {/* Balance Sheet Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="pt-6">
                <p className="text-sm text-gray-500">Total Assets</p>
                <div className="text-2xl font-bold text-blue-700">{formatMoney(balanceData.assets?.total || 0, businessCurrency)}</div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-red-500">
              <CardContent className="pt-6">
                <p className="text-sm text-gray-500">Total Liabilities</p>
                <div className="text-2xl font-bold text-red-700">{formatMoney(balanceData.liabilities?.total || 0, businessCurrency)}</div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-green-500">
              <CardContent className="pt-6">
                <p className="text-sm text-gray-500">Net Equity</p>
                <div className={`text-2xl font-bold ${(balanceData.equity?.net_equity || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                  {formatMoney(balanceData.equity?.net_equity || 0, businessCurrency)}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Assets Detail */}
          <Card>
            <CardHeader>
              <CardTitle className="text-blue-700">Assets</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  ['Revenue Collected', balanceData.assets?.revenue_collected],
                  ['Deposits Received', balanceData.assets?.deposits_received],
                  ['Cash Injections', balanceData.assets?.cash_injections],
                  ['Accounts Receivable', balanceData.assets?.accounts_receivable],
                ].map(([label, value]) => (
                  <div key={label} className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-700">{label}</span>
                    <span className="font-medium">${(value || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                  </div>
                ))}
                <div className="flex justify-between items-center py-2 font-bold text-blue-700">
                  <span>Total Assets</span>
                  <span>${(balanceData.assets?.total || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Liabilities Detail */}
          <Card>
            <CardHeader>
              <CardTitle className="text-red-700">Liabilities</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(balanceData.liabilities?.expense_breakdown || {}).map(([cat, amt]) => (
                  <div key={cat} className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-700">{cat}</span>
                    <span className="font-medium text-red-600">${(amt || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                  </div>
                ))}
                <div className="flex justify-between items-center py-2 font-bold text-red-700">
                  <span>Total Liabilities</span>
                  <span>${(balanceData.liabilities?.total || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      {!loading && reportType === 'cashflow' && cashFlowData && (
        <div className="space-y-6" data-testid="cash-flow-report">
          {renderSubsidiaryBreakdown(cashFlowData, 'cashflow')}
          {/* Cash Flow Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-l-4 border-l-green-500">
              <CardContent className="pt-6">
                <p className="text-sm text-gray-500">Total Inflows</p>
                <div className="text-2xl font-bold text-green-700">{formatMoney(cashFlowData.inflows?.total || 0, businessCurrency)}</div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-red-500">
              <CardContent className="pt-6">
                <p className="text-sm text-gray-500">Total Outflows</p>
                <div className="text-2xl font-bold text-red-700">{formatMoney(cashFlowData.outflows?.total_expenses || 0, businessCurrency)}</div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="pt-6">
                <p className="text-sm text-gray-500">Net Cash Flow</p>
                <div className={`text-2xl font-bold ${(cashFlowData.net_cash_flow || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                  {formatMoney(cashFlowData.net_cash_flow || 0, businessCurrency)}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Inflows & Outflows Detail */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-green-700">Cash Inflows</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-700">Invoice Payments</span>
                    <span className="font-medium text-green-600">${(cashFlowData.inflows?.invoice_payments || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-700">Cash Injections</span>
                    <span className="font-medium text-green-600">${(cashFlowData.inflows?.cash_injections || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                  </div>
                  {cashFlowData.transfers?.count > 0 && (
                    <div className="flex justify-between items-center py-2 border-b border-gray-100" data-testid="cashflow-transfers-line">
                      <span className="text-gray-700">Inter-division Transfers (net)</span>
                      <span className="font-medium text-blue-600">
                        {cashFlowData.transfers.eliminated > 0 && cashFlowData.transfers.in === 0 && cashFlowData.transfers.out === 0
                          ? 'eliminated on consolidation'
                          : `${(cashFlowData.transfers.in - cashFlowData.transfers.out) >= 0 ? '+' : '-'}$${Math.abs(cashFlowData.transfers.in - cashFlowData.transfers.out).toLocaleString(undefined, {minimumFractionDigits: 2})}`}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-center py-2 font-bold text-green-700">
                    <span>Total</span>
                    <span>${(cashFlowData.inflows?.total || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-red-700">Cash Outflows</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(cashFlowData.outflows?.top_categories || []).map((item) => (
                    <div key={item.category} className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-700">{item.category}</span>
                      <span className="font-medium text-red-600">${(item.amount || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center py-2 font-bold text-red-700">
                    <span>Total</span>
                    <span>${(cashFlowData.outflows?.total_expenses || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Monthly Breakdown */}
          {cashFlowData.monthly_breakdown?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Monthly Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-gray-200">
                        <th className="text-left py-2 px-3">Month</th>
                        <th className="text-right py-2 px-3 text-green-700">Inflow</th>
                        <th className="text-right py-2 px-3 text-red-700">Outflow</th>
                        <th className="text-right py-2 px-3">Net</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cashFlowData.monthly_breakdown.map((m, i) => (
                        <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-2 px-3 font-medium">{m.month}</td>
                          <td className="py-2 px-3 text-right text-green-600">{formatMoney(m.inflow || 0, businessCurrency)}</td>
                          <td className="py-2 px-3 text-right text-red-600">{formatMoney(m.outflow || 0, businessCurrency)}</td>
                          <td className={`py-2 px-3 text-right font-medium ${m.net >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                            {formatMoney(m.net || 0, businessCurrency)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
      {!loading && reportType === 'profit-loss' && renderProfitLoss()}
      {!loading && reportType === 'division-pnl' && renderDivisionPnl()}
      {!loading && reportType === 'trial-balance' && accountingData?._type === reportType && <TrialBalanceReport data={accountingData} />}
      {!loading && reportType === 'general-ledger' && accountingData?._type === reportType && <GeneralLedgerReport data={accountingData} accountFilter={glAccount} onAccountChange={setGlAccount} />}
      {!loading && reportType === 'ar-aging' && accountingData?._type === reportType && <AgingReport data={accountingData} kind="ar" />}
      {!loading && reportType === 'ap-aging' && accountingData?._type === reportType && <AgingReport data={accountingData} kind="ap" />}
    </div>
  );
};

export default Reports;
